import { useState, useEffect, useRef } from 'react';

interface LongevityBotProps {
  size?: number;
  className?: string;
  mode?: 'listening' | 'processing' | 'success' | 'concern' | 'happy' | 'streak' | 'signin' | 'splash' | 'payment';
  streakDays?: number;
}

const LongevityBot = ({ 
  size = 350, 
  className = "",
  mode = "listening",
  streakDays = 1
}: LongevityBotProps) => {
  const [pose, setPose] = useState({ 
    eyeX: 0, eyeY: 0, 
    headX: 0, headY: 0, 
    bodyRotate: 0 
  });
  
  const [isBlinking, setIsBlinking] = useState(false);
  const robotRef = useRef<HTMLDivElement>(null);

  // --- MOUSE TRACKING ---
  useEffect(() => {
    const animatedModes = ['processing', 'splash', 'payment']; 
    if (animatedModes.includes(mode) || (mode === 'streak' && streakDays >= 30)) {
         setPose({ eyeX: 0, eyeY: 0, headX: 0, headY: 0, bodyRotate: 0 });
         return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (!robotRef.current) return;
      const rect = robotRef.current.getBoundingClientRect();
      const mouseX = (event.clientX - (rect.left + rect.width / 2));
      const mouseY = (event.clientY - (rect.top + rect.height / 2));
      
      setPose({ 
        eyeX: Math.min(Math.max(mouseX / 10, -16), 16), 
        eyeY: Math.min(Math.max(mouseY / 10, -12), 12),
        headX: Math.min(Math.max(mouseX / 18, -20), 20),
        headY: Math.min(Math.max(mouseY / 18, -15), 15),
        bodyRotate: Math.min(Math.max(mouseX / 60, -5), 5) 
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mode, streakDays]);

  // --- BLINKING ---
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const blinkLoop = () => {
      setIsBlinking(true);
      setTimeout(() => setIsBlinking(false), 150);
      timeoutId = setTimeout(blinkLoop, Math.random() * 4000 + 2000);
    };
    const initialTimeout = setTimeout(blinkLoop, 2000);
    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(timeoutId);
    };
  }, []);

  // --- ANIMATION HELPERS ---
  const getBodyAnimationClass = () => {
      if (mode === 'streak') {
          if (streakDays >= 30) return 'animate-god-mode';
          if (streakDays >= 15) return 'animate-float'; 
          if (streakDays >= 7) return 'animate-vibrate';
          if (streakDays >= 3) return 'animate-bounce-happy';
          return 'animate-breathe';
      }
      switch(mode) {
          case 'payment': return 'animate-fly-superman';
          case 'success': return 'animate-bounce-happy';
          case 'happy': return 'animate-float'; 
          case 'signin': return 'animate-dance';
          case 'splash': return 'animate-peek'; 
          case 'processing': return 'animate-sway';
          case 'concern': return 'animate-slump';
          default: return 'animate-breathe';
      }
  };

  // --- RENDERERS ---

  // 1. CAPE
  const renderCape = () => {
      if (mode !== 'payment') return null;
      return (
          <g transform="translate(80, 280) rotate(45)">
              <path 
                d="M 0,0 Q 50,20 100,-10 L 120,80 Q 50,100 0,60 Z" 
                fill="#f97316" 
                stroke="#ea580c"
                strokeWidth="2"
              >
                  <animate attributeName="d" 
                    values="M 0,0 Q 50,20 100,-10 L 120,80 Q 50,100 0,60 Z; 
                            M 0,0 Q 60,0 110,-20 L 130,70 Q 60,110 0,60 Z; 
                            M 0,0 Q 50,20 100,-10 L 120,80 Q 50,100 0,60 Z" 
                    dur="0.4s" 
                    repeatCount="indefinite" 
                  />
              </path>
          </g>
      );
  };

  // 2. LEGS
  const renderLegs = () => {
      if (mode !== 'payment') return null;
      return (
          <g transform="translate(200, 390)">
             {/* Left Leg */}
             <path 
                d="M -35,-10 L -40,30 C -40,45 -20,45 -20,30 L -15,-10 Z" 
                fill="#0f766e" 
             />
             {/* Right Leg */}
             <path 
                d="M 15,-10 L 20,30 C 20,45 40,45 40,30 L 35,-10 Z" 
                fill="#0f766e" 
             />
          </g>
      );
  };

  // 3. ARMS
  const renderArms = () => {
      if (mode === 'payment') {
          return (
              <g>
                  {/* Left Arm (Tucked Back) */}
                  <ellipse cx="100" cy="320" rx="20" ry="35" fill="#0f766e" transform="rotate(30 100 320)" />
                  {/* Right Arm (Stretched Forward) */}
                  <path 
                    d="M 280,300 L 340,250" 
                    stroke="#0f766e" 
                    strokeWidth="35" 
                    strokeLinecap="round" 
                  />
              </g>
          );
      }
      return (
        <>
            <ellipse cx="85" cy="330" rx="25" ry="40" fill="#0f766e" transform="rotate(-15 85 330)" />
            <ellipse cx="315" cy="330" rx="25" ry="40" fill="#0f766e" transform="rotate(15 315 330)" />
        </>
      );
  };

  // 4. CREDIT CARD
  const renderCard = () => {
      if (mode !== 'payment') return null;
      return (
          <g transform="translate(320, 230) rotate(30)"> 
              <circle cx="0" cy="25" r="22" fill="#0f766e" />
              <g transform="translate(-10, 0)">
                <rect x="0" y="0" width="80" height="50" rx="6" fill="#1e293b" stroke="#475569" strokeWidth="1" />
                <rect x="10" y="15" width="12" height="10" rx="2" fill="#fbbf24" />
                <rect x="0" y="5" width="80" height="8" fill="#000" opacity="0.3" />
                <rect x="10" y="35" width="40" height="4" rx="1" fill="#94a3b8" />
                <ellipse cx="5" cy="25" rx="8" ry="12" fill="#0d9488" />
              </g>
          </g>
      );
  };

  const renderFaceContent = () => {
      if (mode === 'streak') {
          if (streakDays >= 30) return (<g><circle cx="-40" cy="-10" r="30" fill="url(#godModeGlow)" opacity="0.6" /><circle cx="40" cy="-10" r="30" fill="url(#godModeGlow)" opacity="0.6" /><g transform="translate(-40, -15) scale(1.2)"><path d="M -15,-10 L -10,10 L 0,-5 L 10,10 L 15,-10 L 15,15 L -15,15 Z" fill="#fcd34d" stroke="#fbbf24" strokeWidth="2" /><circle cx="0" cy="-10" r="2" fill="#fff" /></g><g transform="translate(40, -15) scale(1.2)"><path d="M -15,-10 L -10,10 L 0,-5 L 10,10 L 15,-10 L 15,15 L -15,15 Z" fill="#fcd34d" stroke="#fbbf24" strokeWidth="2" /><circle cx="0" cy="-10" r="2" fill="#fff" /></g><path d="M -20,35 Q 0,45 20,35" fill="none" stroke="#fcd34d" strokeWidth="3" strokeLinecap="round" /></g>);
          if (streakDays >= 15) return (<g><g transform="translate(-40, -10)"><path d="M 0,-20 L 5,-5 L 20,0 L 5,5 L 0,20 L -5,5 L -20,0 L -5,-5 Z" fill="#c084fc" stroke="#a855f7" strokeWidth="2"><animateTransform attributeName="transform" type="rotate" from="0 0 0" to="360 0 0" dur="4s" repeatCount="indefinite" /></path></g><g transform="translate(40, -10)"><path d="M 0,-20 L 5,-5 L 20,0 L 5,5 L 0,20 L -5,5 L -20,0 L -5,-5 Z" fill="#c084fc" stroke="#a855f7" strokeWidth="2"><animateTransform attributeName="transform" type="rotate" from="0 0 0" to="-360 0 0" dur="4s" repeatCount="indefinite" /></path></g><path d="M -20,30 Q 0,45 20,30" fill="none" stroke="#c084fc" strokeWidth="3" strokeLinecap="round" /></g>);
          if (streakDays >= 7) return (<g><g transform="translate(-40, -10)"><path d="M 5,-20 L -10,0 L 0,0 L -5,20 L 10,0 L 0,0 Z" fill="#fbbf24" stroke="#f59e0b" strokeWidth="2" /></g><g transform="translate(40, -10)"><path d="M 5,-20 L -10,0 L 0,0 L -5,20 L 10,0 L 0,0 Z" fill="#fbbf24" stroke="#f59e0b" strokeWidth="2" /></g><path d="M -15,35 L -5,40 L 5,35 L 15,40" fill="none" stroke="#fbbf24" strokeWidth="3" strokeLinecap="round" /></g>);
          if (streakDays >= 3) return (<g><g transform="translate(-40, -5)"><path d="M 0,15 Q -10,15 -10,5 Q -10,-10 0,-20 Q 10,-10 10,5 Q 10,15 0,15 Z" fill="#f97316" /><path d="M 0,10 Q -5,10 -5,5 Q -5,-5 0,-10 Q 5,-5 5,5 Q 5,10 0,10 Z" fill="#fef08a" /></g><g transform="translate(40, -5)"><path d="M 0,15 Q -10,15 -10,5 Q -10,-10 0,-20 Q 10,-10 10,5 Q 10,15 0,15 Z" fill="#f97316" /><path d="M 0,10 Q -5,10 -5,5 Q -5,-5 0,-10 Q 5,-5 5,5 Q 5,10 0,10 Z" fill="#fef08a" /></g><path d="M -20,35 Q 0,45 20,35" fill="none" stroke="#f97316" strokeWidth="3" strokeLinecap="round" /></g>);
          return (<g transform={`translate(${pose.eyeX * 0.2}, ${pose.eyeY * 0.2})`}><g transform="translate(-55, -10)"><ellipse cx="0" cy="0" rx="32" ry="38" fill="white" filter="url(#softGlow)" /><ellipse cx="0" cy="0" rx="18" ry="22" fill="#0ea5e9" /><ellipse cx="0" cy="0" rx="10" ry="12" fill="#0c4a6e" /><circle cx="-8" cy="-12" r="6" fill="white" opacity="0.9" /></g><g transform="translate(55, -10)"><ellipse cx="0" cy="0" rx="32" ry="38" fill="white" filter="url(#softGlow)" /><ellipse cx="0" cy="0" rx="18" ry="22" fill="#0ea5e9" /><ellipse cx="0" cy="0" rx="10" ry="12" fill="#0c4a6e" /><circle cx="-8" cy="-12" r="6" fill="white" opacity="0.9" /></g><g transform="translate(-55, 35)"><path d="M 0,-10 L 4,0 L 0,10 L -4,0 Z" fill="#facc15" stroke="#eab308" strokeWidth="1"><animateTransform attributeName="transform" type="scale" values="1; 1.3; 1" dur="1.2s" repeatCount="indefinite" /></path></g><g transform="translate(55, 35)"><path d="M 0,-10 L 4,0 L 0,10 L -4,0 Z" fill="#facc15" stroke="#eab308" strokeWidth="1"><animateTransform attributeName="transform" type="scale" values="1; 1.3; 1" dur="1.2s" begin="0.6s" repeatCount="indefinite" /></path></g><path d="M -35,65 Q 0,85 35,65" fill="none" stroke="#2dd4bf" strokeWidth="4" strokeLinecap="round" filter="url(#softGlow)" /></g>);
      }

      if (mode === 'signin') {
        return (<g transform={`translate(${pose.eyeX * 0.2}, ${pose.eyeY * 0.2})`}><g transform="translate(-55, -10)"><ellipse cx="0" cy="0" rx="32" ry="38" fill="white" filter="url(#softGlow)" /><ellipse cx="0" cy="0" rx="18" ry="22" fill="#0ea5e9" /><ellipse cx="0" cy="0" rx="10" ry="12" fill="#0c4a6e" /><circle cx="-8" cy="-12" r="6" fill="white" opacity="0.9" /><path d="M -25,-45 Q 0,-35 25,-45" fill="none" stroke="#0f766e" strokeWidth="5" strokeLinecap="round" transform="rotate(10)" /></g><g transform="translate(55, -10)"><ellipse cx="0" cy="0" rx="32" ry="38" fill="white" filter="url(#softGlow)" /><ellipse cx="0" cy="0" rx="18" ry="22" fill="#0ea5e9" /><ellipse cx="0" cy="0" rx="10" ry="12" fill="#0c4a6e" /><circle cx="-8" cy="-12" r="6" fill="white" opacity="0.9" /><path d="M -25,-45 Q 0,-35 25,-45" fill="none" stroke="#0f766e" strokeWidth="5" strokeLinecap="round" transform="rotate(-10)" /></g><g transform="translate(0, 65)"><path d="M -20,0 Q 0,10 20,-5" fill="none" stroke="#2dd4bf" strokeWidth="4" strokeLinecap="round" filter="url(#softGlow)" /></g></g>);
      }

      const isSquint = mode === 'success' || mode === 'happy' || mode === 'signin' || mode === 'payment';
      
      return (
        <g transform={`translate(${pose.eyeX * 0.2}, ${pose.eyeY * 0.2})`}>
            <g transform="translate(-55, -10)">
                {isSquint ? (
                    <path d="M -25,10 Q 0,-15 25,10" fill="none" stroke="#0ea5e9" strokeWidth="5" strokeLinecap="round" />
                ) : (
                    <>
                        <ellipse cx="0" cy="0" rx="32" ry="38" fill="white" filter="url(#softGlow)" />
                        {mode === 'concern' && <path d="M -32,-10 Q 0,15 32,-10 L 32,-40 L -32,-40 Z" fill="#0f172a" />}
                        <g transform={mode === 'processing' || mode === 'splash' ? "translate(0,0)" : ""}>
                            {(mode === 'processing' || mode === 'splash') && <animateTransform attributeName="transform" type="translate" values="-8,0; 8,0; -8,0" dur="2s" repeatCount="indefinite" />}
                            <ellipse cx="0" cy="0" rx="18" ry="22" fill="#0ea5e9" />
                            <ellipse cx="0" cy="0" rx="10" ry="12" fill="#0c4a6e" />
                            <circle cx="-8" cy="-12" r="6" fill="white" opacity="0.9" />
                        </g>
                    </>
                )}
            </g>
            <g transform="translate(55, -10)">
                {isSquint ? (
                    <path d="M -25,10 Q 0,-15 25,10" fill="none" stroke="#0ea5e9" strokeWidth="5" strokeLinecap="round" />
                ) : (
                    <>
                         <ellipse cx="0" cy="0" rx="32" ry="38" fill="white" filter="url(#softGlow)" />
                        {mode === 'concern' && <path d="M -32,-10 Q 0,15 32,-10 L 32,-40 L -32,-40 Z" fill="#0f172a" />}
                        <g transform={mode === 'processing' || mode === 'splash' ? "translate(0,0)" : ""}>
                            {(mode === 'processing' || mode === 'splash') && <animateTransform attributeName="transform" type="translate" values="-8,0; 8,0; -8,0" dur="2s" repeatCount="indefinite" />}
                            <ellipse cx="0" cy="0" rx="18" ry="22" fill="#0ea5e9" />
                            <ellipse cx="0" cy="0" rx="10" ry="12" fill="#0c4a6e" />
                            <circle cx="-8" cy="-12" r="6" fill="white" opacity="0.9" />
                        </g>
                    </>
                )}
            </g>
            <g transform="translate(0, 65)">
                 {mode === 'concern' ? (
                     <path d="M -30,20 Q 0,0 30,20" fill="none" stroke="#2dd4bf" strokeWidth="4" strokeLinecap="round" />
                 ) : mode === 'processing' || mode === 'splash' ? (
                     <path d="M -15,10 Q 0,15 15,10" fill="none" stroke="#2dd4bf" strokeWidth="4" strokeLinecap="round" />
                 ) : (
                     <path d="M -35,0 Q 0,25 35,0" fill="none" stroke="#2dd4bf" strokeWidth="4" strokeLinecap="round" filter="url(#softGlow)" />
                 )}
            </g>
        </g>
      );
  };

  const renderChest = () => {
      if (mode === 'streak') return (<g transform="translate(200, 305)"><text x="0" y="5" textAnchor="middle" fill={streakDays >= 30 ? "#fff" : "#0f766e"} fontSize="32" fontWeight="bold" fontFamily="monospace">{streakDays}D</text></g>);
      if (mode === 'signin') return (<g transform="translate(200, 305)"><text x="0" y="8" textAnchor="middle" fill="#0f766e" fontSize="26" fontWeight="800" fontFamily="sans-serif" letterSpacing="1" style={{ textShadow: '0px 2px 4px rgba(0,0,0,0.1)' }}>WELCOME</text></g>);
      if (mode === 'happy') return ( <g transform="translate(200, 300)"><circle r="12" fill="#fcd34d" stroke="#d97706" strokeWidth="2" /><g stroke="#f59e0b" strokeWidth="2" strokeLinecap="round">{[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (<line key={i} x1="0" y1="-18" x2="0" y2="-24" transform={`rotate(${angle})`}><animate attributeName="y2" values="-24;-28;-24" dur="2s" begin={`${i * 0.1}s`} repeatCount="indefinite" /></line>))}</g><animateTransform attributeName="transform" type="rotate" from="0 200 300" to="360 200 300" dur="10s" repeatCount="indefinite" /></g>);
      if (mode === 'processing') return ( <g transform="translate(200, 300)"><circle r="25" fill="none" stroke="#115e59" strokeWidth="4" opacity="0.2" /><path d="M 0,-25 A 25,25 0 0 1 25,0" fill="none" stroke="#0f766e" strokeWidth="4" strokeLinecap="round"><animateTransform attributeName="transform" type="rotate" from="0 0 0" to="360 0 0" dur="0.8s" repeatCount="indefinite" /></path></g>);
      if (mode === 'success') return ( <g transform="translate(200, 300) scale(0.8)"><circle r="30" fill="#0f766e" /><path d="M -15,0 L -5,10 L 15,-10" fill="none" stroke="white" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" /></g>);
      if (mode === 'concern') return ( <g transform="translate(200, 305)"><path d="M 0,-20 L 0,5" fill="none" stroke="#ef4444" strokeWidth="6" strokeLinecap="round" /><circle cx="0" cy="15" r="4" fill="#ef4444" /></g>);
      
      return (
        <g>
            <path d="M 140,300 L 170,300 L 180,270 L 195,330 L 210,300 L 220,300 L 260,300" fill="none" stroke="#0f766e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="220" strokeDashoffset="220">
                <animate attributeName="stroke-dashoffset" values="220; 0; 0" keyTimes="0; 0.85; 1" dur="2.5s" repeatCount="indefinite" calcMode="linear" />
                <animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.9; 1" dur="2.5s" repeatCount="indefinite" />
            </path>
            <circle r="3" fill="#0d9488"><animateMotion path="M 140,300 L 170,300 L 180,270 L 195,330 L 210,300 L 220,300 L 260,300" dur="2.5s" repeatCount="indefinite" keyPoints="0;1" keyTimes="0;0.85" calcMode="linear" /><animate attributeName="opacity" values="1; 1; 0" keyTimes="0; 0.9; 1" dur="2.5s" repeatCount="indefinite" /></circle>
        </g>
      );
  };

  const renderClouds = () => (
      <g transform="translate(0, 320)" opacity="0.95">
          <g className="animate-cloud-drift">
              <path d="M -400,50 Q -300,0 -200,50 Q -100,20 0,50 Q 50,0 100,50 Q 150,20 200,50 Q 250,10 300,50 Q 350,20 400,50 Q 500,10 600,50 Q 700,20 800,50 L 800,150 L -400,150 Z" fill="#ccfbf1" />
              <path d="M -450,80 Q -350,30 -250,80 Q -150,40 -50,80 Q 0,30 50,80 Q 100,40 150,80 Q 200,20 250,80 Q 300,40 350,80 Q 400,30 450,80 Q 550,40 650,80 Q 750,30 850,80 L 850,200 L -450,200 Z" fill="white" />
          </g>
      </g>
  );

  return (
    <div ref={robotRef} className={`relative select-none ${className}`} style={{ width: size, height: size }}>
      
      <style>{`
        @keyframes breathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.02); } }
        @keyframes sway { 0%, 100% { transform: rotate(-2deg); } 50% { transform: rotate(2deg); } }
        @keyframes bounce-happy { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-15px); } }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes slump { 0% { transform: translateY(0) scaleY(1); } 100% { transform: translateY(10px) scaleY(0.95); } }
        @keyframes vibrate { 0% { transform: translate(0,0); } 20% { transform: translate(-2px,2px); } 40% { transform: translate(-2px,-2px); } 60% { transform: translate(2px,2px); } 80% { transform: translate(2px,-2px); } 100% { transform: translate(0,0); } }
        @keyframes god-mode { 0% { transform: translateY(0) scale(1); filter: drop-shadow(0 0 10px #fcd34d); } 50% { transform: translateY(-20px) scale(1.05); filter: drop-shadow(0 0 25px #fcd34d); } 100% { transform: translateY(0) scale(1); filter: drop-shadow(0 0 10px #fcd34d); } }
        @keyframes dance { 0%, 100% { transform: translateY(0) rotate(0); } 25% { transform: translateY(-10px) rotate(-5deg); } 50% { transform: translateY(0) rotate(0); } 75% { transform: translateY(-10px) rotate(5deg); } }
        @keyframes peek { 0%, 100% { transform: translateY(15px); } 50% { transform: translateY(5px); } }
        @keyframes cloud-drift { from { transform: translateX(0); } to { transform: translateX(-150px); } }
        @keyframes fly-superman { 
            0%, 100% { transform: translateY(0) rotate(45deg); } 
            50% { transform: translateY(-20px) rotate(45deg); } 
        }

        .animate-breathe { animation: breathe 4s ease-in-out infinite; transform-origin: bottom center; }
        .animate-sway { animation: sway 3s ease-in-out infinite; transform-origin: bottom center; }
        .animate-bounce-happy { animation: bounce-happy 0.6s cubic-bezier(0.28, 0.84, 0.42, 1) infinite; }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-slump { animation: slump 0.5s ease-out forwards; transform-origin: bottom center; }
        .animate-vibrate { animation: vibrate 0.3s linear infinite; }
        .animate-god-mode { animation: god-mode 4s ease-in-out infinite; }
        .animate-dance { animation: dance 1s ease-in-out infinite; transform-origin: bottom center; }
        .animate-peek { animation: peek 3s ease-in-out infinite; transform-origin: bottom center; }
        .animate-cloud-drift { animation: cloud-drift 20s linear infinite; }
        .animate-fly-superman { animation: fly-superman 2s ease-in-out infinite; transform-origin: center; }
        
        .mascot-transition { transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
      `}</style>

      <svg viewBox="0 0 400 400" className="w-full h-full drop-shadow-2xl" style={{ overflow: 'visible' }}>
        <defs>
          <linearGradient id="healthBodyGradient" x1="20%" y1="0%" x2="80%" y2="100%">
            <stop offset="0%" stopColor="#2dd4bf" />
            <stop offset="100%" stopColor="#0f766e" />
          </linearGradient>
          <linearGradient id="chestScreenGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            {streakDays >= 30 && mode === 'streak' ? (
                <> <stop offset="0%" stopColor="#fef3c7" /><stop offset="100%" stopColor="#fcd34d" /> </>
            ) : (
                <> <stop offset="0%" stopColor="#ccfbf1" /><stop offset="100%" stopColor="#99f6e4" /> </>
            )}
          </linearGradient>
          <radialGradient id="cheekGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#f472b6" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#f472b6" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="godModeGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#fcd34d" stopOpacity="0" />
          </radialGradient>
          <filter id="softGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        <g className={`${getBodyAnimationClass()} mascot-transition`}>
            {/* CAPE (Payment Mode) - Render behind everything */}
            {renderCape()}

            {/* BODY */}
            <g transform={`rotate(${pose.bodyRotate}, 200, 400)`} className="mascot-transition">
                {/* Robot Body */}
                <g transform={mode === 'splash' ? "translate(0, 20)" : ""}>
                    <path d="M 100,240 C 80,260 70,300 80,340 C 90,380 130,400 200,400 C 270,400 310,380 320,340 C 330,300 320,260 300,240 L 100,240 Z" fill="url(#healthBodyGradient)" />
                    
                    {/* LEGS (Behind everything else inside body) */}
                    {renderLegs()}

                    {renderArms()}
                    <path d="M 130,240 L 270,240 C 275,280 260,340 250,350 L 150,350 C 140,340 125,280 130,240 Z" fill="url(#chestScreenGradient)" />
                    {renderChest()}
                    {renderCard()}
                </g>

                {/* HEAD */}
                <g transform={`translate(${pose.headX}, ${pose.headY}) rotate(${pose.headX * 0.5})`} className="mascot-transition"> 
                  <g transform={mode === 'concern' ? "rotate(-10)" : "rotate(0)"} className="mascot-transition">
                      <g transform={mode === 'splash' ? "translate(0, 20)" : ""}>
                        <path d="M -140,-80 C -150,0 -130,110 0,115 C 130,110 150,0 140,-80 C 130,-140 -130,-140 -140,-80 Z" fill="#2dd4bf" stroke="#115e59" strokeWidth="2" transform="translate(200, 150)" />
                        <path d="M -125,-70 C -135,0 -115,100 0,105 C 115,100 135,0 125,-70 C 115,-120 -115,-120 -125,-70 Z" fill="#0f172a" transform="translate(200, 150)" />
                        <circle cx="200" cy="65" r="5" fill="#334155" />
                        <circle cx="200" cy="65" r="2" fill="#60a5fa" />
                        <g transform={`translate(200, 150) scale(1, ${isBlinking && mode !== 'success' ? 0.1 : 1})`} className="transition-transform duration-100">
                            {renderFaceContent()}
                        </g>
                      </g>
                  </g>
                </g> 
            </g>
        </g>
        
        {/* --- CLOUD LAYER (Only visible in Splash Mode) --- */}
        {mode === 'splash' && renderClouds()}
        
      </svg>
    </div>
  );
};

export default LongevityBot;